import { CSSProperties } from 'react';

/**
 * Font family mapping from email-builder-js
 * Source: @usewaypoint/block-text/dist/index.js
 */
function getFontFamily(fontFamily?: string | null): string | undefined {
  if (!fontFamily) return undefined;

  switch (fontFamily) {
    case 'MODERN_SANS':
      return '"Helvetica Neue", "Arial Nova", "Nimbus Sans", Arial, sans-serif';
    case 'BOOK_SANS':
      return 'Optima, Candara, "Noto Sans", source-sans-pro, sans-serif';
    case 'ORGANIC_SANS':
      return 'Seravek, "Gill Sans Nova", Ubuntu, Calibri, "DejaVu Sans", source-sans-pro, sans-serif';
    case 'GEOMETRIC_SANS':
      return 'Avenir, "Avenir Next LT Pro", Montserrat, Corbel, "URW Gothic", source-sans-pro, sans-serif';
    case 'HEAVY_SANS':
      return 'Bahnschrift, "DIN Alternate", "Franklin Gothic Medium", "Nimbus Sans Narrow", sans-serif-condensed, sans-serif';
    case 'ROUNDED_SANS':
      return 'ui-rounded, "Hiragino Maru Gothic ProN", Quicksand, Comfortaa, Manjari, "Arial Rounded MT Bold", Calibri, source-sans-pro, sans-serif';
    case 'MODERN_SERIF':
      return 'Charter, "Bitstream Charter", "Sitka Text", Cambria, serif';
    case 'BOOK_SERIF':
      return '"Iowan Old Style", "Palatino Linotype", "URW Palladio L", P052, serif';
    case 'MONOSPACE':
      return '"Nimbus Mono PS", "Courier New", "Cutive Mono", monospace';
    default:
      return undefined;
  }
}

/**
 * Converts email-builder-js style object to React CSS properties
 *
 * Email-builder-js uses a custom style format:
 * - padding: object with top/bottom/left/right numbers
 * - fontSize: number (no unit)
 * - fontFamily: enum string that needs mapping
 *
 * This function converts to standard React CSSProperties format
 */
export function convertEmailBuilderStylesToCSS(style?: {
  color?: string | null;
  backgroundColor?: string | null;
  fontSize?: number | null;
  fontFamily?: string | null;
  fontWeight?: 'bold' | 'normal' | null;
  textAlign?: 'left' | 'center' | 'right' | null;
  padding?: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } | null;
}): CSSProperties {
  if (!style) return {};

  const result: CSSProperties = {};

  // Handle padding object -> individual padding properties
  if (style.padding) {
    result.paddingTop = `${style.padding.top}px`;
    result.paddingBottom = `${style.padding.bottom}px`;
    result.paddingLeft = `${style.padding.left}px`;
    result.paddingRight = `${style.padding.right}px`;
  }

  // Handle fontSize number -> px string
  if (style.fontSize !== undefined && style.fontSize !== null) {
    result.fontSize = `${style.fontSize}px`;
  }

  // Handle fontFamily enum -> actual font stack
  const fontFamily = getFontFamily(style.fontFamily);
  if (fontFamily) {
    result.fontFamily = fontFamily;
  }

  // Copy over properties that are already in correct format
  if (style.color) {
    result.color = style.color;
  }
  if (style.backgroundColor) {
    result.backgroundColor = style.backgroundColor;
  }
  if (style.fontWeight) {
    result.fontWeight = style.fontWeight;
  }
  if (style.textAlign) {
    result.textAlign = style.textAlign;
  }

  return result;
}
